import { Badge } from "@bcc/components/ui/badge";
import { Button } from "@bcc/components/ui/button";
import { Calendar } from "@bcc/components/ui/calendar";
import { Input } from "@bcc/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@bcc/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@bcc/components/ui/select";
import type { InvoiceFilters } from "@bcc/lib/types/invoice";
import { formatDateToISO } from "@client-shared/utils/format";
import { InvoiceDateType, InvoiceDateTypes, InvoiceStatus } from "@shared/types";
import { CalendarIcon, Search, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ChangeEvent, KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  setFilterBarContainerTestId,
  setFilterBarDateFilterButtonTestId,
  setFilterBarSearchButtonTestId,
  setFilterBarSearchInputTestId,
  setFilterBarStatusButtonTestId,
} from "./filter-bar.utils";

interface FilterBarProps {
  filters: InvoiceFilters;
  onFiltersChange: (filters: InvoiceFilters) => void;
  onSearch: (query: string) => void;
  testId?: string;
}

const DEBOUNCE_DELAY_MS = 300;

const statusFilters = [
  {
    status: "Paid" as InvoiceStatus,
    color: "bg-green-500",
    labelKey: "paid",
  },
  {
    status: "Unpaid" as InvoiceStatus,
    color: "bg-yellow-500",
    labelKey: "unpaid",
  },
  {
    status: "Unposted" as InvoiceStatus,
    color: "bg-red-500",
    labelKey: "unposted",
  },
];

export function FilterBar({ filters, onFiltersChange, onSearch, testId }: Readonly<FilterBarProps>) {
  const [searchQuery, setSearchQuery] = useState(filters.query || "");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current);
    };
  }, []);

  const triggerSearchImmediate = useCallback(
    (query: string) => {
      clearTimeout(debounceRef.current);
      onSearch(query);
    },
    [onSearch]
  );

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const val = event.target.value;
      setSearchQuery(val);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => onSearch(val), DEBOUNCE_DELAY_MS);
    },
    [onSearch]
  );

  const handleSearchKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") triggerSearchImmediate(searchQuery);
    },
    [triggerSearchImmediate, searchQuery]
  );

  const handleSearchClick = useCallback(() => triggerSearchImmediate(searchQuery), [triggerSearchImmediate, searchQuery]);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [dateFrom, setDateFrom] = useState<Date | undefined>(filters.dateFrom ? new Date(filters.dateFrom) : undefined);

  const [dateTo, setDateTo] = useState<Date | undefined>(filters.dateTo ? new Date(filters.dateTo) : undefined);
  const [dateType, setDateType] = useState<InvoiceDateType>(filters.dateType ?? InvoiceDateTypes.ORDER_DATE);
  const { t } = useTranslation();
  const dateNow = new Date();

  const isDateDisabled = (date: Date) => date > dateNow;

  const handleStatusToggle = (status: InvoiceStatus) => {
    const currentStatuses = filters.status || [];
    const newStatuses = currentStatuses.includes(status) ? currentStatuses.filter((s) => s !== status) : [...currentStatuses, status];
    onFiltersChange({
      ...filters,
      status: newStatuses.length > 0 ? newStatuses : undefined,
    });
  };

  const handleDateTypeChange = (value: InvoiceDateType) => {
    setDateType(value);
  };

  const handleDateFilterApply = () => {
    onFiltersChange({
      ...filters,
      dateFrom: formatDateToISO(dateFrom),
      dateTo: formatDateToISO(dateTo),
      dateType,
    });
    setShowDateFilter(false);
  };

  const handleClearFilters = () => {
    clearTimeout(debounceRef.current);
    setSearchQuery("");
    setDateFrom(undefined);
    setDateTo(undefined);
    setDateType(InvoiceDateTypes.ORDER_DATE);
    onFiltersChange({});
    onSearch("");
  };

  const activeFilterCount = (filters.status?.length || 0) + (filters.dateFrom || filters.dateTo ? 1 : 0);

  return (
    <div data-testid={testId ?? setFilterBarContainerTestId()} className="space-y-4">
      {/* Search Bar */}
      <div className="flex gap-2">
        <div className="flex flex-1 gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid={setFilterBarSearchInputTestId()}
              placeholder={t("finance.filterBar.searchPlaceholder")}
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              className="pl-9"
            />
          </div>
          <Button data-testid={setFilterBarSearchButtonTestId()} onClick={handleSearchClick}>
            {t("finance.filterBar.search")}
          </Button>
        </div>
        <Popover open={showDateFilter} onOpenChange={setShowDateFilter}>
          <PopoverTrigger>
            <Button data-testid={setFilterBarDateFilterButtonTestId()} variant="outline" className="gap-2">
              <CalendarIcon className="h-4 w-4" />
              {t("finance.filterBar.dateFilter")}
              {(filters.dateFrom || filters.dateTo) && (
                <Badge variant="secondary" className="ml-1">
                  1
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-4" align="end">
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("finance.filterBar.dateType")}</label>
                <Select value={dateType} onValueChange={handleDateTypeChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={InvoiceDateTypes.INVOICE_DATE}>{t("finance.filterBar.invoiceDate")}</SelectItem>
                    <SelectItem value={InvoiceDateTypes.LAST_SETTLE_DATE}>{t("finance.filterBar.paymentDate")}</SelectItem>
                    <SelectItem value={InvoiceDateTypes.ORDER_DATE}>{t("finance.filterBar.orderDate")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-4">
                <div className="space-y-2">
                  <div className="text-sm font-medium">{t("finance.filterBar.fromDate")}</div>
                  <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} disabled={isDateDisabled} defaultMonth={dateFrom} />
                </div>
                <div className="space-y-2">
                  <div className="text-sm font-medium">{t("finance.filterBar.toDate")}</div>
                  <Calendar mode="single" selected={dateTo} onSelect={setDateTo} disabled={isDateDisabled} defaultMonth={dateTo} />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleDateFilterApply} className="flex-1">
                  {t("finance.filterBar.apply")}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setDateFrom(undefined);
                    setDateTo(undefined);
                    setDateType(InvoiceDateTypes.ORDER_DATE);
                    onFiltersChange({
                      ...filters,
                      dateFrom: undefined,
                      dateTo: undefined,
                      dateType: undefined,
                    });
                  }}
                >
                  {t("finance.filterBar.clear")}
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
        {activeFilterCount > 0 && (
          <Button variant="ghost" onClick={handleClearFilters} className="gap-2">
            <X className="h-4 w-4" />
            {t("finance.filterBar.clearAll")}
          </Button>
        )}
      </div>
      {/* Filter Chips */}
      <div className="flex flex-wrap gap-2">
        {statusFilters.map(({ status, color, labelKey }) => (
          <Button
            key={status}
            data-testid={setFilterBarStatusButtonTestId(status)}
            variant={filters.status?.includes(status) ? "default" : "outline"}
            size="sm"
            onClick={() => handleStatusToggle(status)}
            className="gap-1"
          >
            <div className={`h-2 w-2 rounded-full ${color}`} />
            {t(`finance.filterBar.status.${labelKey}`)}
          </Button>
        ))}
      </div>
    </div>
  );
}
